import { useTemplateStore } from '@/store/template.store';
import { useAnimationControl } from '@/composables/useAnimationControl';
import { sortFields } from '@/utils/sortFields';
import { addMultipleFields } from '@/commons/fabricjs/FabricFieldHelper';
import { updateFontFamily } from '@/commons/fabricjs/FabricTextHelper';
import { orderElementList } from '@/commons/fabricjs/FabricPositionHelper';
import { Events } from '@/services/Events';
import { BAT_SCALE_FACTOR } from '@/littleWedding/projectConstantes';
import { useConfigStore } from '@/store/config.store';
import { LOCKED_FOREGROUND_GROUP_NAME } from '@/littleWedding/fabricGroupsName';
import { keepLockedBackgroundObjectGroupToBack } from '@/commons/fabricjs/FabricGroupHelper';
import { isFieldOnSide } from '@/utils/fieldSide';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { IField } from '@/typescript/interfaces/IField';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITextField } from '@/typescript/interfaces/ITextField';
import type { WFabricWorld } from '@/littleWedding/WFabricWorld';
import type { Group } from 'fabric';

function useField() {
    const templateStore = useTemplateStore();
    const { cutpathFieldList, displayGrid, isAdmin, isProductEdition } = storeToRefs(useConfigStore());
    const { stopAnimation } = useAnimationControl();
    const { selectedField, selectedFieldId, selectedTemplateConfiguration } = storeToRefs(templateStore);
    const isFormVisible = ref(false);

    function hideForm() {
        isFormVisible.value = false;
    }
    onMounted(() => {
        Events.on('image-transformed', updateImageFieldTransform);
    });

    onUnmounted(() => {
        Events.off('image-transformed', updateImageFieldTransform);
    });

    function showForm() {
        stopAnimation();
        isFormVisible.value = true;
    }

    function deleteField(field: IField) {
        templateStore.removeField(field);
    }

    function toggleLock(field: IField) {
        const fieldToEdit = { ...field };
        fieldToEdit.editable = !field.editable;
        templateStore.updateField(fieldToEdit);
    }

    function duplicateField(field: IField) {
        const newField: IField = structuredClone({ ...field, id: crypto.randomUUID() });

        newField.transform.position.x += 100;
        newField.transform.position.y += 100;

        templateStore.addField(newField);
    }

    const filteredFields = computed(() => {
        const merged = [...selectedTemplateConfiguration.value.fields, ...cutpathFieldList.value].filter(
            (filteredField) =>
                filteredField.transform.position.x + filteredField.transform.position.y !== 0 ||
                (filteredField as IImageField)?.url !== undefined ||
                (filteredField as ITextField)?.defaultText !== undefined,
        );
        // Dedup by id so any duplicate field in data (e.g. legacy templates saved before commit 28e465a)
        // can't cause overlaid renders on the canvas / BAT.
        const seen = new Set<string>();
        const deduped = merged.filter((field) => {
            if (seen.has(field.id)) return false;
            seen.add(field.id);
            return true;
        });
        return sortFields(deduped);
    });

    function getFieldsRecto(): IField[] {
        return filteredFields.value.filter((field) => isFieldOnSide(field, 'recto'));
    }

    function getFieldsVerso(): IField[] {
        return filteredFields.value.filter((field) => isFieldOnSide(field, 'verso'));
    }

    function loadFields<T extends WFabricWorld>(
        fields: (IImageField | ITextField)[],
        params: {
            fabricWorld: T;
            isAdmin?: boolean;
            isProductEdition?: boolean;
            scaleFactor?: number;
            select?: boolean;
        },
        updateMaterial: () => void,
    ) {
        const { fabricWorld, scaleFactor = BAT_SCALE_FACTOR, select = false } = params;
        return addMultipleFields(fields, {
            fabricWorldInstance: fabricWorld,
            addToCanvas: true,
            select,
            isAdmin: params.isAdmin,
            isProductEdition: params.isProductEdition,
        })
            .then((fElements) => {
                fElements.forEach(async (fElem) => {
                    if (!fElem) return;
                    // Cut path fields produce no fabric element, so indexes of `fElements` and `fields` can
                    // diverge: always match the source field by id.
                    const field = fields.find((candidate) => candidate.id === fElem.id);
                    const { left, scaleX, scaleY, top } = fElem;
                    fElem.left = left * scaleFactor;
                    fElem.top = top * scaleFactor;

                    if (fElem?.type === 'image') {
                        fElem.scaleX = scaleX * scaleFactor;
                        fElem.scaleY = scaleY * scaleFactor;
                    } else if (field) {
                        updateFontFamily({
                            fabricCanvas: fabricWorld.fabricCanvas,
                            fText: fElem,
                            fontFamily: (field as ITextField).font,
                            initial: true,
                        });
                    }
                    // This callback runs once every image of the template has loaded, so the customer may
                    // already be typing in a text. Fabric flips `selectable` to false while a text is in
                    // editing mode, so the lock state must come from the field, never from the fabric object:
                    // reading `fElem.selectable` here turned the text being edited into a locked one
                    // (unselectable for good, or moved into the locked group before 1.2.6), the customer then
                    // re-created it and both copies ended up overlaid in the BAT/PDF.
                    const isLocked = field !== undefined && !field.editable && !params.isAdmin;
                    if (isLocked) {
                        // Locked fields stay on the canvas: moving them into the locked group used to push them
                        // behind every other field, so a background field covering the whole customization zone
                        // hid them all. They only need to be non-interactive, their layer order is the one
                        // defined in the template and applied by updateFieldsAndOrder.
                        fElem.set({ selectable: false, evented: false });
                    }
                    fabricWorld.fabricCanvas!.renderAll();
                });
            })
            .finally(updateMaterial);
    }

    function updateImageFieldTransform(fieldEdited: Partial<IImageField> & { hasQualityWarning: boolean }) {
        const { frameSize, hasQualityWarning, id, transform } = fieldEdited;
        // selectedField
        if (selectedField.value === undefined || selectedField.value?.id !== id) return;
        selectedField.value.transform = { ...(transform ?? selectedField.value.transform) };
        selectedField.value.frameSize = frameSize ?? selectedField.value.frameSize;
        templateStore.updateField(selectedField.value);
        Events.emit('image-quality-warning', { hasQualityWarning });
    }

    function addFieldToCanvas<T extends WFabricWorld>(fabricWorld: T, newField: IImageField | ITextField, updateMaterial: () => void) {
        if (!newField) {
            return;
        }
        loadFields(
            [newField],
            {
                fabricWorld,
                select: true,
                isAdmin: isAdmin.value,
                isProductEdition: isProductEdition.value,
            },
            updateMaterial,
        );
        if (!isProductEdition.value) {
            selectedFieldId.value = newField.id;
        }
        fabricWorld.keepGraduationImageUpFront();
        if (fabricWorld.fabricCanvas) {
            keepLockedBackgroundObjectGroupToBack(fabricWorld.fabricCanvas, LOCKED_FOREGROUND_GROUP_NAME);
        }
        fabricWorld.keepGridImageUpFront();
    }

    function deleteFieldInsideCanvas<T extends WFabricWorld>(
        fabricWorld: T,
        previousFields: (IImageField | ITextField)[],
        updatedFields: (IImageField | ITextField)[],
    ) {
        const suppressedElement = previousFields.find((oldField) => !updatedFields.some((newField) => newField.id === oldField.id));
        if (!suppressedElement) {
            return;
        }
        const object = fabricWorld.getFabricElementById(suppressedElement.id);
        if (object?.type === 'image') {
            Events.emit('image-quality-warning', { hasQualityWarning: false });
        }
        fabricWorld.removeFabricElementById(suppressedElement.id);
        fabricWorld.keepGraduationImageUpFront();
        if (fabricWorld.fabricCanvas) {
            keepLockedBackgroundObjectGroupToBack(fabricWorld.fabricCanvas, LOCKED_FOREGROUND_GROUP_NAME);
        }
        fabricWorld.keepGridImageUpFront();
    }

    function updateFieldsAndOrder<T extends WFabricWorld>(fabricWorld: T, updatedFields: (IImageField | ITextField)[]) {
        const fElements = updatedFields
            .map((field) => fabricWorld.getFabricElementById(field.id))
            .filter((fElemen) => fElemen !== undefined);
        orderElementList(
            fabricWorld.fabricCanvas,
            fElements.filter((el) => el !== undefined),
            updatedFields.map((field) => field.id),
        );
        fabricWorld.setGridHelperVisibility(displayGrid.value);
        fabricWorld.keepGraduationImageUpFront();
        if (fabricWorld.fabricCanvas) {
            keepLockedBackgroundObjectGroupToBack(fabricWorld.fabricCanvas, LOCKED_FOREGROUND_GROUP_NAME);
        }
        const bgGroup = (fabricWorld.fabricCanvas?.getObjects('group') as Group[]).find((group) => {
            return !(group as Group & { id: string })?.id;
        });
        fabricWorld.keepBackgroundMaskUpFront();
        fabricWorld.keepGridImageUpFront();
        if (!bgGroup) return;
        fabricWorld.fabricCanvas?.sendObjectToBack(bgGroup);
    }

    return {
        addFieldToCanvas,
        deleteField,
        deleteFieldInsideCanvas,
        duplicateField,
        filteredFields,
        getFieldsRecto,
        getFieldsVerso,
        hideForm,
        isFormVisible,
        loadFields,
        showForm,
        toggleLock,
        updateFieldsAndOrder,
    };
}

export { useField };
